import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { getFactionStoredWrestlers } from './factionWrestlers'
import { getEffectiveFactionMemberStatus } from './factionLineupRole'
import {
  getFactionMainEventerStatus,
  rosterRequiresLineup,
  shouldBlockFactionScoringForMissingLineup
} from './factionScoring'

const toUniqueIds = values =>
  Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)))

const chunkIds = (ids = [], size = 10) => {
  const chunks = []
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size))
  }
  return chunks
}

const mapSnapshotDocs = snapshot =>
  snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))

const fetchCollectionDocsByIds = async ({ db, collectionName, ids = [] }) => {
  const uniqueIds = toUniqueIds(ids)
  if (uniqueIds.length === 0) return []

  const snapshots = await Promise.all(
    chunkIds(uniqueIds).map(chunk =>
      getDocs(
        query(collection(db, collectionName), where('__name__', 'in', chunk))
      )
    )
  )

  return snapshots.flatMap(mapSnapshotDocs)
}

const buildUserFactionIds = userData =>
  toUniqueIds([
    ...(Array.isArray(userData?.factionIds) ? userData.factionIds : []),
    userData?.factionId,
    userData?.favoriteFactionId,
    userData?.roadToFactionId
  ])

export const resolveFactionAlertInjuryFlag = value =>
  Boolean(value?.isInjured ?? value?.is_injured ?? value?.injured ?? false)

export const resolveFactionAlertMemberStatus = ({
  faction,
  member,
  referenceDate = new Date()
}) =>
  getEffectiveFactionMemberStatus({
    faction,
    wrestlerId: member?.id || member?.wrestlerId || null,
    fallbackStatus: member?.status || null,
    referenceDate
  })

export const isFactionMemberInjuryAlerting = ({
  faction,
  member,
  referenceDate = new Date()
}) => {
  if (!resolveFactionAlertInjuryFlag(member)) return false

  return (
    resolveFactionAlertMemberStatus({
      faction,
      member,
      referenceDate
    }) !== 'Backstage'
  )
}

export const mapFactionMembersWithAlertData = ({
  faction,
  wrestlersById = {}
}) =>
  getFactionStoredWrestlers(faction).map(member => {
    const wrestler = wrestlersById[member.id] || {}
    return {
      ...member,
      ...wrestler,
      id: member.id,
      wrestlerId: member.id,
      isInjured:
        resolveFactionAlertInjuryFlag(wrestler) ||
        resolveFactionAlertInjuryFlag(member)
    }
  })

export const buildFactionAlertState = ({
  faction,
  roster = null,
  members = []
}) => {
  if (!faction) {
    return {
      hasAnyAlert: false,
      hasLineupAlert: false,
      hasMainEventerAlert: false,
      hasInjuryAlert: false,
      mainEventerStatus: null
    }
  }

  const hasLineupAlert =
    rosterRequiresLineup(roster, faction) &&
    shouldBlockFactionScoringForMissingLineup({
      faction,
      roster
    })
  const mainEventerStatus = getFactionMainEventerStatus({ faction, roster })
  const hasMainEventerAlert = Boolean(mainEventerStatus)
  const hasInjuryAlert = (members || []).some(member =>
    isFactionMemberInjuryAlerting({
      faction,
      member
    })
  )

  return {
    hasAnyAlert: hasLineupAlert || hasMainEventerAlert || hasInjuryAlert,
    hasLineupAlert,
    hasMainEventerAlert,
    hasInjuryAlert,
    mainEventerStatus
  }
}

export const loadUserFactionAlertState = async ({ db, userId }) => {
  if (!db || !userId) {
    return {
      hasAnyAlerts: false,
      alertsByRosterId: {}
    }
  }

  const userSnap = await getDoc(doc(db, 'users', userId))
  if (!userSnap.exists()) {
    return {
      hasAnyAlerts: false,
      alertsByRosterId: {}
    }
  }

  const userData = userSnap.data()
  const requestedFactionIds = buildUserFactionIds(userData)
  let factions = []

  if (requestedFactionIds.length > 0) {
    factions = await fetchCollectionDocsByIds({
      db,
      collectionName: 'factions',
      ids: requestedFactionIds
    })
  } else {
    const ownedFactionsSnap = await getDocs(
      query(collection(db, 'factions'), where('ownerUid', '==', userId))
    )
    factions = mapSnapshotDocs(ownedFactionsSnap)
  }

  if (factions.length === 0) {
    return {
      hasAnyAlerts: false,
      alertsByRosterId: {}
    }
  }

  const rosterIds = factions.map(faction => faction?.rosterId).filter(Boolean)
  const memberIds = toUniqueIds(
    factions.flatMap(faction =>
      getFactionStoredWrestlers(faction)
        .map(member => member?.id)
        .filter(Boolean)
    )
  )

  const [rosters, wrestlers] = await Promise.all([
    fetchCollectionDocsByIds({
      db,
      collectionName: 'rosters',
      ids: rosterIds
    }),
    fetchCollectionDocsByIds({
      db,
      collectionName: 'wrestlers',
      ids: memberIds
    })
  ])

  const rostersById = rosters.reduce((acc, roster) => {
    acc[roster.id] = roster
    return acc
  }, {})
  const wrestlersById = wrestlers.reduce((acc, wrestler) => {
    acc[wrestler.id] = wrestler
    return acc
  }, {})

  const alertsByRosterId = factions.reduce((acc, faction) => {
    if (!faction?.rosterId) return acc

    const alertState = buildFactionAlertState({
      faction,
      roster: rostersById[faction.rosterId] || null,
      members: mapFactionMembersWithAlertData({
        faction,
        wrestlersById
      })
    })

    if (alertState.hasAnyAlert) {
      acc[faction.rosterId] = alertState
    }

    return acc
  }, {})

  return {
    hasAnyAlerts: Object.keys(alertsByRosterId).length > 0,
    alertsByRosterId
  }
}
